import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Starts a live catalog resync and polls its job status until done.
export const useCatalogResync = (token, onComplete) => {
  const [job, setJob] = useState(null);
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const poll = useCallback((jobId, collectionName) => {
    stop();
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/sync-publitas/status/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
        setJob({ ...data, collectionName });
        if (data.status === 'completed' || data.status === 'failed') {
          stop();
          if (data.status === 'completed') {
            toast.success(`${collectionName}: ${data.synced} new, ${data.updated} updated colors from catalog`);
            onComplete?.();
          } else {
            toast.error(`Resync failed: ${data.error || 'unknown error'}`);
          }
        }
      } catch {
        stop();
      }
    }, 2000);
  }, [token, stop, onComplete]);

  const start = useCallback(async (collection) => {
    if (!collection?.catalog_url) { toast.error('No catalog URL set. Edit the collection to add one.'); return; }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/fabric-collections/collections/${collection.id}/resync-from-catalog`,
        { catalog_url: collection.catalog_url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJob({ job_id: data.job_id, status: 'queued', progress: 'Starting...', collectionName: collection.name });
      toast.info(data.already_running ? 'Resync already running for this collection' : `Scraping ${data.sync_type === 'dw' ? 'DW' : 'Publitas'} catalog for ${collection.name}...`);
      poll(data.job_id, collection.name);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start resync');
    }
  }, [token, poll]);

  return { job, start, dismiss: () => setJob(null) };
};
